import { ComponentIds, Vector, Ray, MeasurementUtils } from '@inter-ikea-core/core';
import { LineType } from '@inter-ikea-betrakta/viewer';
import { LineMeasureWidget } from '@inter-ikea-betrakta/viewer/dist/modules/MeasurementWidget';
import { PlannerEcsMultiSystem } from '../System';
import { HelpLinesBoxColliderComponent, MeasurementsEnum } from '../../../';
import EventComponent from '../../components/EventComponent';
import HelpLinesOuterMeasurementsComponent from '../../components/HelpLinesOuterMeasurementsComponent';
import HelpLinesOuterMeasurementsLineCenterComponent from '../../components/HelpLinesOuterMeasurementsLineCenterComponent';
import { getLabelPositions } from './HelpLinesLineLabelUtils';
const scale = 1000;
const color = { r: 0 / 255, g: 0 / 255, b: 0 / 255 };
const directionToTop = new Vector(0, 1, 0);
export class HelpLinesOuterMeasurementSystem extends PlannerEcsMultiSystem {
    constructor(coreApi) {
        super(coreApi, 'HelpLinesOuterMeasurementSystem', [
            ComponentIds.SOMETHING_IS_DRAGGED_COMPONENT_ID,
            ComponentIds.IS_DRAGGED_COMPONENT_ID,
            ComponentIds.SELECTED_COMPONENT_ID,
            EventComponent.id
        ]);
        this.coreApi = coreApi;
        this.lines = [];
        this.currentlyDragged = undefined;
        this.isSelected = undefined;
        this.measurementsEntity = coreApi.getServices().entityManager.createEntity();
    }
    onAdd(entity, newComponent) {
        if (this.coreApi.entities.getScene()?.hasComponent(HelpLinesOuterMeasurementsComponent.id)) {
            switch (newComponent.typeId) {
                case ComponentIds.SOMETHING_IS_DRAGGED_COMPONENT_ID:
                    const draggedEntity = newComponent.entity;
                    if (draggedEntity && draggedEntity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
                        this.currentlyDragged = draggedEntity;
                        this.coreApi.scene?.setOnlyShowMeasurements([
                            MeasurementsEnum.HelpLinesFrameOuterMeasurements
                        ]);
                    }
                    break;
                case ComponentIds.SELECTED_COMPONENT_ID:
                    if (entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
                        this.isSelected = entity;
                        this.onRemoveLines();
                        this.setLineCenters(this.getLineMeasureWidgets(this.isSelected));
                    }
                    break;
            }
        }
    }
    onUpdate(entity, oldComponent, newComponent) {
        switch (newComponent.typeId) {
            case EventComponent.id:
                {
                    const component = newComponent;
                    if (component.event === 'history' || component.event === 'update') {
                        if (this.currentlyDragged) {
                            this.onRemoveLines();
                            this.setLineCenters(this.getLineMeasureWidgets(this.currentlyDragged));
                        }
                        if (this.isSelected) {
                            this.onRemoveLines();
                            this.setLineCenters(this.getLineMeasureWidgets(this.isSelected));
                        }
                    }
                }
                break;
            default:
                break;
        }
    }
    onRemove(entity, oldComponent) {
        switch (oldComponent.typeId) {
            case ComponentIds.IS_DRAGGED_COMPONENT_ID:
                this.currentlyDragged = undefined;
                this.coreApi.scene?.setOnlyShowMeasurements([]);
                if (!this.isSelected) {
                    this.onRemoveLines();
                }
                break;
            case ComponentIds.SELECTED_COMPONENT_ID:
                this.isSelected = undefined;
                if (!this.currentlyDragged) {
                    this.onRemoveLines();
                }
                break;
        }
    }
    /**
     * Creates and updates the measurement lines
     * to the nearest entity above a frame.
     * @entity The entity to measure from
     */
    getLineMeasureWidgets(entity) {
        if (!entity)
            return [];
        const viewer = this.coreApi.ipexViewer;
        const helpLinesSystem = this.coreApi.getSystem('HelpLinesSystem');
        const shapeDimensions = helpLinesSystem?.getShapeDimensions();
        const entityOutsideMeasurements = MeasurementUtils.getOutsideMeasurementSingle(entity);
        if (entityOutsideMeasurements) {
            const measureToShow = this.calculateMeasureToShow(entity, shapeDimensions);
            if (measureToShow?.vector) {
                return [this.getLineMeasureWidget(measureToShow?.vector, viewer, measureToShow?.distance)];
            }
        }
        return [];
    }
    calculateMeasureToShow(entity, shapeDimensions) {
        if (!shapeDimensions) {
            return null;
        }
        const { leftTop, rightTop } = this.getTopStartPositions(entity);
        const leftDistance = this.getShortestDistanceToFunctionalZone(leftTop);
        const rightDistance = this.getShortestDistanceToFunctionalZone(rightTop);
        // Last if statement needs to be there because left measurement can be bigger than right when outside shape
        // and without it the right measurement disapears sometimes when dragging a frame close to shape left line
        if (this.pointIsInsideShape(rightTop, shapeDimensions) && // Right starting point must be inside shape
            rightDistance !== 0 && // Right measurement distance cannot be 0
            (rightDistance < leftDistance || !this.pointIsInsideShape(leftTop, shapeDimensions)) // Right distance is less than left or left start point is outside shape.
        ) {
            return { vector: rightTop, distance: rightDistance };
        }
        else {
            if (this.pointIsInsideShape(leftTop, shapeDimensions)) {
                return { vector: leftTop, distance: leftDistance };
            }
        }
        return null;
    }
    /**
     * Check if point x position is inside the shape shape horizontally
     * @param point Measure point position
     * @param vs The shape points/corners
     * @returns Truthy if x is inside shape
     */
    pointIsInsideShape(point, vs) {
        // ray-casting algorithm based on
        // https://wrf.ecse.rpi.edu/Research/Short_Notes/pnpoly.html
        // If shape is not on back wall and Z becomes larger than 1 it is considerd not inside of shape.
        if (point.Z > 1)
            return false;
        const x = point.X, y = point.Y;
        let inside = false;
        for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
            const xi = vs[i].X, yi = vs[i].Y;
            const xj = vs[j].X, yj = vs[j].Y;
            const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
            if (intersect)
                inside = !inside;
        }
        return inside;
    }
    /**
     * Returns shortest distance between all HelpLinesBoxColliderComponents in scene and provided start vector
     * @param start Position to raycast/measure from.
     * @returns Distance to closest HelpLinesBoxColliderComponent above start position
     */
    getShortestDistanceToFunctionalZone(start) {
        let minDistance = 0;
        const helpLinesColliderBoxes = this.coreApi.entities.getByComponent(HelpLinesBoxColliderComponent.id);
        helpLinesColliderBoxes.forEach(hcb => {
            const polyhedron = hcb.getComponent(ComponentIds.FUNCTIONAL_MARGIN_COMPONENT_ID)?.worldPolyhedron;
            const ray = new Ray(start, directionToTop);
            const result = polyhedron?.raycast(ray);
            if (result) {
                const distance = ray.origin.distanceTo(result.point);
                if (minDistance === 0 || distance < minDistance) {
                    minDistance = distance;
                }
            }
        });
        return minDistance;
    }
    getLineMeasureWidget(start, viewer, distance) {
        const rightTopViewer = this.toViewerVector(start);
        return new LineMeasureWidget(viewer, rightTopViewer, {
            x: rightTopViewer.x,
            y: rightTopViewer.y + distance / scale,
            z: rightTopViewer.z
        }, color, 0.01, this.toViewerVector(directionToTop), 0.01, LineType.DASHED);
    }
    toViewerVector(v) {
        return { x: v.X / scale, y: v.Y / scale, z: v.Z / scale };
    }
    getTopStartPositions(entity) {
        const entityOutsideMeasurements = MeasurementUtils.getOutsideMeasurementSingle(entity);
        if (!entityOutsideMeasurements)
            return { leftTop: new Vector(0, 0, 0), rightTop: new Vector(0, 0, 0) };
        const leftTop = new Vector(entityOutsideMeasurements.max.X - entityOutsideMeasurements.width, entityOutsideMeasurements.max.Y, entityOutsideMeasurements.max.Z - entityOutsideMeasurements.depth + 1);
        const rightTop = new Vector(entityOutsideMeasurements.max.X, entityOutsideMeasurements.max.Y, entityOutsideMeasurements.max.Z - entityOutsideMeasurements.depth + 1);
        return { leftTop, rightTop };
    }
    /**
     * Updates the labels with the distance of the line
     * aswell as the position of the label.
     */
    setLineCenters(lines) {
        this.lines = lines;
        if (this.lines.length > 0) {
            const lineCenterPoints = this.lines.map(line => ({ start: line.start, end: line.end }));
            const labelPositions = getLabelPositions(this.coreApi, lineCenterPoints, {
                x: 0,
                y: 0,
                z: 0
            });
            this.measurementsEntity.addComponent(new HelpLinesOuterMeasurementsLineCenterComponent(labelPositions));
        }
    }
    /**
     * Runs when you release mouse press from the selected entity.
     * Removes both lines and labels from the scene.
     */
    onRemoveLines() {
        Object.values(this.lines).forEach(line => {
            line.dispose();
        });
        this.lines = [];
        this.measurementsEntity.removeComponent(HelpLinesOuterMeasurementsLineCenterComponent.id);
    }
}
//# sourceMappingURL=HelpLinesOuterMeasurementSystem.js.map